import React, { useEffect, useRef } from 'react';

import {
  Stack,
  SvgCaretDown,
  SvgCaretUp,
} from '@grubhubprod/gh-cookbook-react';
import {
  Button,
  Copy,
  Flex,
  Squish,
} from '@grubhubprod/gh-cookbook-react/dist';
import { useForm } from 'react-hook-form/dist/index.ie11';
import classnames from 'classnames';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { Dropdown } from 'src/components/Dropdown/Dropdown';
import { AvailableOrderTypesEnum } from 'src/components/enterprise/features/OrderHistory/OrderList/TableRow';

export interface OrderSelectionProps {
  onSelectedOrderTypesChange: (order_types: string[]) => void;
  availableOrderTypes: string[];
  selectedOrderTypes?: string[];
}

export function OrderSelection({
  onSelectedOrderTypesChange,
  availableOrderTypes,
  selectedOrderTypes = [],
}: OrderSelectionProps): JSX.Element {
  const dropdownRef = useRef(null);
  const hasSelectedOrderTypes =
    selectedOrderTypes.length > 0 &&
    selectedOrderTypes.length < availableOrderTypes.length;

  const { register, handleSubmit, reset } = useForm({
    mode: 'onChange',
  });

  useEffect(() => {
    reset({ order_types: selectedOrderTypes });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderTypes]);

  function onSubmit({ order_types }) {
    onSelectedOrderTypesChange(order_types);
    dropdownRef.current.close();
  }

  function onClear() {
    onSelectedOrderTypesChange([]);
    dropdownRef.current.close();
  }

  return (
    <Dropdown
      ref={dropdownRef}
      closeMenuOnClick={false}
      toggle={({ expanded }) => (
        <Flex
          alignItems="center"
          backgroundColor="globals.white"
          className={classnames('input')}
          color="secondaryText"
        >
          <Stack as="span" x="xxx-sm">
            <Copy
              variant="body"
              color={hasSelectedOrderTypes ? 'primaryText' : 'secondaryText'}
            >
              Order type
            </Copy>
          </Stack>
          {hasSelectedOrderTypes && (
            <Stack as="span" x="xxx-sm">
              <Copy variant="body" color="primaryText">
                ({selectedOrderTypes.length})
              </Copy>
            </Stack>
          )}
          {expanded ? (
            <SvgCaretUp size="16px" pointerEvents="none" />
          ) : (
            <SvgCaretDown size="16px" pointerEvents="none" />
          )}
        </Flex>
      )}
      menu={({ show }) =>
        show && (
          <Flex
            position="relative"
            flexDirection="column"
            width="150px"
            dimension="1"
            zIndex={10}
            bg="globals.white"
          >
            <Squish inset="md" bg="globals.white" display="block">
              {availableOrderTypes.map((orderTypeKey) => (
                <Stack
                  y="sm"
                  key={orderTypeKey}
                  data-testid="order-type-filter"
                >
                  <Checkbox
                    cancelLabelBottomMargin
                    id={orderTypeKey}
                    name="order_types"
                    label={AvailableOrderTypesEnum[orderTypeKey]}
                    ref={register}
                    value={orderTypeKey}
                  />
                </Stack>
              ))}
              <Flex justifyContent="space-between">
                <Button
                  variant="tertiary"
                  buttonSize="medium"
                  onClick={onClear}
                >
                  Clear
                </Button>
                <Button
                  variant="tertiary"
                  buttonSize="medium"
                  onClick={handleSubmit(onSubmit)}
                >
                  Apply
                </Button>
              </Flex>
            </Squish>
          </Flex>
        )
      }
    />
  );
}
